import { JobType, useGetJobsQuery } from '@/generated/graphql';
import {
  getConversationId,
  getCurrentMessage,
  getItemParticipants,
} from '@/utils/microsoft-graph';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { JobsStateJobFragment } from '@shared/contexts/pluginContexts/EmailThreadContext';
import { useInterval } from '@shared/hooks/useInterval';
import { useJobPolling } from '@shared/hooks/useJobPolling';
import {
  getHasUnconfirmedQuoteAndOrder,
  jobFragmentsToJobsState,
} from '@shared/plugin/utils/optimisticJobs';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';
import { getItemSharedMailbox } from './utils/getItemSharedMailbox';
import { getViewedMailboxAddress } from './utils/getViewedMailboxAddress';

type OutlookAdaptorState = {
  jobs: JobsStateJobFragment[];
  jobsLoading: boolean;
  refetchThreadContext: () => Promise<void>;
  jobTypes: Record<string, JobType>;
  storeJobType: (jobType: JobType, jobId?: string) => void;
  item?: Office.MessageRead;
  itemBody?: string;
  conversationId?: string;
  isSharedInbox: boolean;
  sharedMailboxAddress?: string;
  viewedMailboxAddress: string;
  sharedInboxCandidates: string[];
  selectSharedInboxCandidate: (email?: string) => void;
  hasUnconfirmedQuote: boolean;
  hasUnconfirmedOrder: boolean;
  isComposeMode: boolean;
};

const POLL_INTERVAL_INFREQUENT_SECONDS = 30 * 1000;

/**
 * Detects if we're in compose mode by checking for compose-specific APIs
 */
function checkIsComposeMode(): boolean {
  const { item } = Office.context.mailbox;
  if (!item) return false;

  return 'getComposeTypeAsync' in item;
}

/**
 * Provides the jobs related to the currently focused email thread.
 * Additionally, polls on an interval with conditional frequency
 * depending on the number of processing jobs.
 */
export const useOutlookAdaptor = (): OutlookAdaptorState => {
  const location = useLocation();
  const [conversationId, setConversationId] = useState<string>();
  const [item, setItem] = useState<Office.MessageRead>();
  const [itemBody, setItemBody] = useState<string>();
  const [jobTypes, setJobTypes] = useState<Record<string, JobType>>({});
  const [isComposeMode, setIsComposeMode] = useState(false);
  const { defaultOrgId } = useMeContext();
  const [sharedMailboxAddress, setSharedMailboxAddress] = useLocalStorage<
    string | undefined
  >('shared-inbox-owner', undefined);
  const [isSharedInbox, setIsSharedInbox] = useState(
    Boolean(sharedMailboxAddress)
  );
  const [sharedInboxCandidates, setSharedInboxCandidates] = useState<string[]>(
    []
  );
  const viewedMailboxAddress = getViewedMailboxAddress(sharedMailboxAddress);

  const { data, startPolling, stopPolling, loading } = useGetJobsQuery({
    variables: {
      input: {
        organizationIds: defaultOrgId ? [defaultOrgId] : [],
        outlookFilter: {
          conversationId,
          sharedInbox: sharedMailboxAddress,
          mailboxAddress: viewedMailboxAddress,
        },
        filterUnconfirmedJobs: false,
      },
    },
    skip: !conversationId,
    pollInterval: POLL_INTERVAL_INFREQUENT_SECONDS,
  });
  useJobPolling({
    data,
    startPolling,
    stopPolling,
  });

  const refetchThreadContext = async () => {
    const readItem = getCurrentMessage();
    const { item } = Office.context.mailbox;
    const isCompose = checkIsComposeMode();

    // NOTE(parlato): Compose mode
    if (isCompose && item) {
      setIsComposeMode(true);
      setItemBody('');

      const composeItemForContext = {
        itemId: item.itemId,
        internetMessageId: item.internetMessageId,
        conversationId: item.conversationId,
        sender: {
          emailAddress: Office.context.mailbox.userProfile.emailAddress,
          displayName: Office.context.mailbox.userProfile.displayName,
        },
        itemType: Office.MailboxEnums.ItemType.Message,
      } as Office.MessageRead;
      setItem(composeItemForContext);

      let officeConversationId: string | undefined;
      if (item.conversationId) {
        officeConversationId = Office.context.mailbox.convertToRestId(
          item.conversationId,
          Office.MailboxEnums.RestVersion.v2_0
        );
      }

      if (officeConversationId) {
        setConversationId(officeConversationId);
      } else {
        setConversationId(undefined);
      }
      return;
    }

    // NOTE(parlato): Read mode
    setIsComposeMode(false);

    const conversationId = getConversationId();
    setConversationId(conversationId);

    setItem(readItem);

    if (readItem) {
      readItem.body.getAsync(Office.CoercionType.Text, (res) => {
        if (res.status === Office.AsyncResultStatus.Succeeded) {
          setItemBody(res.value);
        }
      });
    } else {
      setItemBody('');
    }
  };

  useInterval(async () => {
    await refetchThreadContext();
  }, 1000 * 10);

  useEffect(() => {
    refetchThreadContext();
  }, [location.pathname]);

  useEffect(() => {
    if (!Office.context.mailbox) return;
    Office.context.mailbox.addHandlerAsync(
      Office.EventType.ItemChanged,
      refetchThreadContext
    );
  }, []);

  useEffect(() => {
    if (!Office.context.mailbox) return;
    if (item) {
      void getItemSharedMailbox(item).then((address) => {
        setIsSharedInbox(Boolean(address));
        setSharedMailboxAddress(address);
      });
    } else {
      setIsSharedInbox(false);
      setSharedMailboxAddress(undefined);
    }

    // NOTE(parlato): These are candidates for shared mailbox owners
    // in case the above method fails to detect shared mailbox.
    const { participants } = getItemParticipants();
    setSharedInboxCandidates(participants);
  }, [item]);

  const selectSharedInboxCandidate = (email?: string) => {
    setSharedMailboxAddress(email ?? '');
    setIsSharedInbox(Boolean(email));
  };

  const storeJobType = (jobType: JobType, jobId?: string) => {
    if (!jobId) return;

    setJobTypes((prev) => ({
      ...prev,
      [jobId]: jobType,
    }));
  };

  const jobs: JobsStateJobFragment[] = useMemo(() => {
    return jobFragmentsToJobsState(data);
  }, [data]);

  const { hasUnconfirmedQuote, hasUnconfirmedOrder } = useMemo(() => {
    return getHasUnconfirmedQuoteAndOrder(data);
  }, [jobs]);

  return {
    jobs,
    jobsLoading: loading,
    item,
    itemBody,
    conversationId,
    isSharedInbox,
    sharedInboxCandidates,
    jobTypes,
    sharedMailboxAddress,
    viewedMailboxAddress,
    refetchThreadContext,
    selectSharedInboxCandidate,
    storeJobType,
    hasUnconfirmedQuote,
    hasUnconfirmedOrder,
    isComposeMode,
  };
};
